import { ColumnType, Row } from "@asayinc/component-library";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import relativeTime from "dayjs/plugin/relativeTime";

import { IBankruptcyEvent } from "src/store/bankruptcyEvent/types";
import { BankruptcyEventsView } from "./BankruptcyEvents";
import { BANKRUPTCY_EVENTS_VIEW } from "./BankruptcyEvents";
import {
  BANKRUPTCY_CHAPTER_LABEL,
  BankruptcyChapter,
} from "src/constants/labels/bankruptcyEventLabel";

dayjs.extend(relativeTime);
dayjs.extend(utc);

export const UPCOMING_COLUMNS = [
  "issuer",
  "recordDate",
  "filingDate",
  "chapter",
  "claimsAdministrator",
  "created",
];

export const ACTIVE_COLUMNS = [
  "issuer",
  "recordDate",
  "filingDate",
  "chapter",
  "claimsAdministrator",
  "created",
];

export const RECENTLY_COMPLETED_COLUMNS = [
  "issuer",
  "recordDate",
  "filingDate",
  "chapter",
  "claimsAdministrator",
  "created",
];

export const HISTORICAL_COLUMNS = [
  "issuer",
  "recordDate",
  "filingDate",
  "chapter",
  "claimsAdministrator",
  "created",
];

export type ColumnDataHelpers = {
  getSort: (sortKey: string) => "asc" | "desc" | undefined;
  onOrderingChange: (data: unknown, sortKey: string) => void;
};

export type UpcomingColumnData = {
  type: ColumnType;
  issuer: ColumnType;
  recordDate: ColumnType;
  filingDate: ColumnType;
  chapter: ColumnType;
  claimsAdministrator: ColumnType;
  created: ColumnType;
};

export type ActiveColumnData = {
  type: ColumnType;
  issuer: ColumnType;
  recordDate: ColumnType;
  filingDate: ColumnType;
  chapter: ColumnType;
  claimsAdministrator: ColumnType;
  created: ColumnType;
};

export type RecentlyCompletedColumnData = {
  type: ColumnType;
  issuer: ColumnType;
  recordDate: ColumnType;
  filingDate: ColumnType;
  chapter: ColumnType;
  claimsAdministrator: ColumnType;
  created: ColumnType;
};

export type HistoricalColumnData = {
  issuer: ColumnType;
  type: ColumnType;
  recordDate: ColumnType;
  filingDate: ColumnType;
  chapter: ColumnType;
  claimsAdministrator: ColumnType;
  created: ColumnType;
};

export function getColumns(view: BankruptcyEventsView) {
  if (view === BANKRUPTCY_EVENTS_VIEW.UPCOMING) {
    return UPCOMING_COLUMNS;
  } else if (view === BANKRUPTCY_EVENTS_VIEW.ACTIVE) {
    return ACTIVE_COLUMNS;
  } else if (view === BANKRUPTCY_EVENTS_VIEW.RECENTLY_COMPLETED) {
    return RECENTLY_COMPLETED_COLUMNS;
  } else {
    return HISTORICAL_COLUMNS;
  }
}

export function getColumnData(
  view: string,
  { getSort, onOrderingChange }: ColumnDataHelpers
):
  | UpcomingColumnData
  | ActiveColumnData
  | RecentlyCompletedColumnData
  | HistoricalColumnData {
  if (view === BANKRUPTCY_EVENTS_VIEW.UPCOMING) {
    return {
      type: {
        sort: getSort("type"),
        sortFn: (data: unknown) => onOrderingChange(data, "type"),
        style: "text",
        title: "Type",
        tdBodyProps: {
          textProps: {
            weight: 600,
          },
        },
      },
      issuer: {
        sort: getSort("issuer__name"),
        sortFn: (data: unknown) => onOrderingChange(data, "issuer__name"),
        style: "text",
        title: "Issuer",
        tdBodyProps: {
          textProps: {
            weight: 600,
          },
        },
      },
      recordDate: {
        sort: getSort("recordDate"),
        sortFn: (data: unknown) => onOrderingChange(data, "recordDate"),
        style: "text",
        title: "Record Date",
      },
      filingDate: {
        sort: getSort("filingDate"),
        sortFn: (data: unknown) => onOrderingChange(data, "filingDate"),
        style: "text",
        title: "Filing date",
      },
      claimsAdministrator: {
        sort: getSort("claimsAdministrator__name"),
        sortFn: (data: unknown) =>
          onOrderingChange(data, "claimsAdministrator__name"),
        style: "text",
        title: "Claims Administrator",
      },
      created: {
        sort: getSort("created"),
        sortFn: (data: unknown) => onOrderingChange(data, "created"),
        style: "text",
        title: "Created",
      },
      chapter: {
        sort: getSort("chapter"),
        sortFn: (data: unknown) => onOrderingChange(data, "chapter"),
        style: "text",
        title: "Chapter",
      },
    };
  } else {
    return {
      type: {
        sort: getSort("type"),
        sortFn: (data: unknown) => onOrderingChange(data, "type"),
        style: "text",
        title: "Type",
        tdBodyProps: {
          textProps: {
            weight: 600,
          },
        },
      },
      issuer: {
        sort: getSort("issuer__name"),
        sortFn: (data: unknown) => onOrderingChange(data, "issuer__name"),
        style: "text",
        title: "Issuer",
        tdBodyProps: {
          textProps: {
            weight: 600,
          },
        },
      },
      recordDate: {
        sort: getSort("recordDate"),
        sortFn: (data: unknown) => onOrderingChange(data, "recordDate"),
        style: "text",
        title: "Record Date",
      },
      filingDate: {
        sort: getSort("filingDate"),
        sortFn: (data: unknown) => onOrderingChange(data, "filingDate"),
        style: "text",
        title: "Filing date",
      },
      claimsAdministrator: {
        sort: getSort("claimsAdministrator__name"),
        sortFn: (data: unknown) =>
          onOrderingChange(data, "claimsAdministrator__name"),
        style: "text",
        title: "Claims Administrator",
      },
      created: {
        sort: getSort("created"),
        sortFn: (data: unknown) => onOrderingChange(data, "created"),
        style: "text",
        title: "Created",
      },
      chapter: {
        sort: getSort("chapter"),
        sortFn: (data: unknown) => onOrderingChange(data, "chapter"),
        style: "text",
        title: "Chapter",
      },
    };
  }
}

export function getRowData(
  view: string,
  events: IBankruptcyEvent[],
  onRowClick: (event: IBankruptcyEvent) => void
): Row[] {
  const handleRowClick = (data: unknown) => {
    const { event } = data as {
      event: IBankruptcyEvent;
      idx: string;
    };
    onRowClick(event);
  };

  if (view === BANKRUPTCY_EVENTS_VIEW.UPCOMING) {
    return events.map((event, idx) => {
      const {
        id,
        issuer,
        recordDate,
        filingDate,
        chapter,
        claimsAdministrator,
        created,
      } = event;

      return {
        id,
        data: { event, idx },
        rowData: {
          issuer: {
            content: issuer.name,
            clickHandler: handleRowClick,
          },
          recordDate: {
            content: dayjs(recordDate).format("MM/DD/YYYY"),
            clickHandler: handleRowClick,
          },
          filingDate: {
            content: dayjs(filingDate).format("MM/DD/YYYY"),
            clickHandler: handleRowClick,
          },
          claimsAdministrator: {
            content: claimsAdministrator.name,
            clickHandler: handleRowClick,
          },
          chapter: {
            content:
              BANKRUPTCY_CHAPTER_LABEL[chapter as BankruptcyChapter] || chapter,
            clickHandler: handleRowClick,
          },
          created: {
            content: dayjs(created).format("MM/DD/YYYY"),
            clickHandler: handleRowClick,
          },
        },
      };
    });
  } else {
    return events.map((event, idx) => {
      const {
        id,
        issuer,
        recordDate,
        filingDate,
        chapter,
        claimsAdministrator,
        created,
      } = event;

      return {
        id,
        data: { event, idx },
        rowData: {
          issuer: {
            content: issuer.name,
            clickHandler: handleRowClick,
          },
          recordDate: {
            content: dayjs(recordDate).format("MM/DD/YYYY"),
            clickHandler: handleRowClick,
          },
          filingDate: {
            content: dayjs(filingDate).format("MM/DD/YYYY"),
            clickHandler: handleRowClick,
          },
          claimsAdministrator: {
            content: claimsAdministrator.name,
            clickHandler: handleRowClick,
          },
          chapter: {
            content:
              BANKRUPTCY_CHAPTER_LABEL[chapter as BankruptcyChapter] || chapter,
            clickHandler: handleRowClick,
          },
          created: {
            content: dayjs(created).format("MM/DD/YYYY"),
            clickHandler: handleRowClick,
          },
        },
      };
    });
  }
}
